import type { TBookingCart } from '@providers/booking/types'
import type { GenericSimpleBag } from 'service-api/src/types'
import { Types } from 'service-api'
import { ClientAssetService, ClientAssetUsageService } from 'service-api/src/services'
import { isCheckInOutTimeEnabled, isVetEnabled } from 'components-ui/src/shared'

export const buildCheckInOutOrderUpdate = (
  values: { checkInTime?: string; checkOutTime?: string },
  reservation: Types.BookingReservationClientDto
): Pick<Types.UpdateBookingOrderClientInput, 'data'> => {
  if (!isCheckInOutTimeEnabled(reservation.locationBookingConf!)) {
    return {}
  }
  const ci = (values.checkInTime || '').trim()
  const co = (values.checkOutTime || '').trim()
  if (!ci && !co) {
    return {}
  }
  return {
    data: { ...(ci ? { checkInTime: ci } : {}), ...(co ? { checkOutTime: co } : {}) },
  }
}

export const buildOrderInput = (
  values: GenericSimpleBag,
  reservation: Types.BookingReservationClientDto,
  inputCardGroups: Types.FlexBookingConfClientDto[][],
  cart?: Pick<TBookingCart, 'checkInTime' | 'checkOutTime'>
): Types.UpdateBookingOrderClientInput => {
  const data: Types.UpdateBookingOrderClientInput = {
    userData: {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values.email,
      data: {
        mailingAddress: {
          streetAddress: values.streetAddress,
          streetAddress2: values.streetAddress2.trim() === '' ? null : values.streetAddress2,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
          country: values.country,
        },
      },
    },
  }

  const checkInOut = buildCheckInOutOrderUpdate(
    {
      checkInTime: values.checkInTime || cart?.checkInTime,
      checkOutTime: values.checkOutTime || cart?.checkOutTime,
    },
    reservation
  )
  if (checkInOut.data) {
    data.data = { ...(data.data || {}), ...checkInOut.data }
  }

  if (isVetEnabled(reservation.locationBookingConf!)) {
    let { vetName: vn, vetPhone: vp } = values
    vn = vn.trim()
    vp = vp.trim()

    if (vn || vp) {
      const vet = { ...(vn ? { name: vn } : {}), ...(vp ? { phone: vp } : {}) }
      data.data = { ...(data.data || {}), ...vet }
    }
  }

  const fieldNames = inputCardGroups
    .reduce((acc, groups) => acc.concat(groups.map(({ name }) => name)), [] as (string | undefined)[])
    .filter(Boolean) as string[]

  fieldNames.forEach((fieldName) => {
    data.flexBookingConfs ||= {}
    data.flexBookingConfs[fieldName] = [{ value: values[fieldName]?.trim() || '' }]
  })

  return data
}

export const inputCardsFilter = (bookingConf: Types.FlexBookingConfClientDto) => {
  return (
    bookingConf.type === 'FIELD' &&
    bookingConf.target === 'ORDER' &&
    (bookingConf.step === 'BOOKING' || bookingConf.step === 'ALL')
  )
}

export const inputCardGroupsBuilder = (bookingConf: Types.FlexBookingConfClientDto[]) => {
  const groups =
    bookingConf?.filter(inputCardsFilter).reduce<Record<string, Types.FlexBookingConfClientDto[]>>((acc, bc) => {
      const group = bc.group || ''
      acc[group] ||= []
      acc[group].push(bc)
      return acc
    }, {}) || {}
  return Object.values(groups)
}

export const createAsset = ({ accountName, file, orderId }: { accountName: string; file: File; orderId?: string }) =>
  ClientAssetService.create(accountName, 'USER', 'RECORD', file).then((createdAsset) =>
    ClientAssetUsageService.create(accountName, createdAsset.id!, {
      orderId,
      type: 'ORDER_ATTACHMENT',
    })
  )

export const getOfferIdFromErrorMsg = (errorMsg: string) => {
  const match = errorMsg.match(/(c\w{24,})/)
  return match ? match[1] : null
}
